import React, { useState, useEffect } from 'react';
import { Container, Table, Button, Form, Modal, Alert, Spinner, Badge, InputGroup, Tab, Nav } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faKey, faEye, faEyeSlash, faUserShield, faUser } from '@fortawesome/free-solid-svg-icons';
import { API_URL } from '../services/api';
import { toast } from 'react-hot-toast';

const MODULES = {
  transportation: {
    label: 'Transportation (BOLs)',
    permissions: ['full_access'],
    exclusive: false
  },
  warehouse_receipts: {
    label: 'Warehouse Receipts',
    permissions: ['full_access'],
    exclusive: false
  },
  inventory: {
    label: 'Inventory',
    permissions: ['read_only', 'submit', 'approve'],
    exclusive: true // Only one inventory permission allowed
  },
  accounts: {
    label: 'Accounts Management',
    permissions: ['full_access'],
    exclusive: false
  },
  invoices: {
    label: 'Invoices',
    permissions: ['full_access'],
    exclusive: false
  },
  ext_orders: {
    label: 'External Orders',
    permissions: ['full_access'],
    exclusive: false
  }
};

function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    isAdmin: false,
    isActive: true,
    permissions: {}
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  // Clear form when modal opens to prevent browser autofill
  useEffect(() => {
    if (showModal && modalMode === 'add') {
      // Force clear form fields after modal opens
      setTimeout(() => {
        setFormData({
          username: '',
          email: '',
          password: '',
          firstName: '',
          lastName: '',
          isAdmin: false,
          isActive: true,
          permissions: {}
        });
      }, 10);
    }
  }, [showModal, modalMode]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/users`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setModalMode('add');
    setSelectedUser(null);
    setFormData({
      username: '',
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      isAdmin: false,
      isActive: true,
      permissions: {}
    });
    setShowModal(true);
  };

  const handleEdit = (user) => {
    setModalMode('edit');
    setSelectedUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: '',
      firstName: user.firstName,
      lastName: user.lastName,
      isAdmin: user.isAdmin,
      isActive: user.isActive,
      permissions: user.permissions || {}
    });
    setShowModal(true);
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Are you sure you want to delete user "${user.username}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/users/${user.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete user');
      }

      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const url = modalMode === 'add' 
        ? `${API_URL}/api/users` 
        : `${API_URL}/api/users/${selectedUser.id}`;
      
      const method = modalMode === 'add' ? 'POST' : 'PUT';

      const payload = { ...formData };
      
      // Only include password if it's set (for edit mode)
      if (modalMode === 'edit' && !payload.password) {
        delete payload.password;
      }

      const response = await fetch(url, {
        method,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to save user');
      }

      toast.success(`User ${modalMode === 'add' ? 'created' : 'updated'} successfully`);
      setShowModal(false);
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error(error.message);
    }
  };

  const handlePermissionChange = (module, permission, checked) => {
    setFormData(prev => {
      const newPermissions = { ...prev.permissions };
      const moduleConfig = MODULES[module];
      
      // For exclusive modules (like inventory), replace all permissions with the selected one
      if (moduleConfig?.exclusive) {
        if (checked) {
          newPermissions[module] = [permission];
        } else {
          delete newPermissions[module];
        }
      } else {
        // For non-exclusive modules, allow multiple permissions
        if (checked) {
          if (!newPermissions[module]) {
            newPermissions[module] = [];
          }
          if (!newPermissions[module].includes(permission)) {
            newPermissions[module].push(permission);
          }
        } else {
          if (newPermissions[module]) {
            newPermissions[module] = newPermissions[module].filter(p => p !== permission);
            if (newPermissions[module].length === 0) {
              delete newPermissions[module];
            }
          }
        }
      }

      return { ...prev, permissions: newPermissions };
    });
  };

  const renderPermissions = (permissions) => {
    if (!permissions || Object.keys(permissions).length === 0) {
      return <span className="text-muted">None</span>;
    }

    return (
      <div className="d-flex flex-wrap gap-1">
        {Object.entries(permissions).map(([module, perms]) => (
          <Badge key={module} bg="info" className="text-wrap">
            {MODULES[module]?.label || module}: {perms.join(', ')}
          </Badge>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container fluid className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>
          <FontAwesomeIcon icon={faUserShield} className="me-2" />
          Portal Users
        </h2>
        <Button variant="primary" onClick={handleAdd}>
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          Add User
        </Button>
      </div>

      <Alert variant="info">
        <strong>Note:</strong> Only Admin users can access this page and manage other users. 
        Sub-users will have access based on their assigned permissions.
      </Alert>

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Username</th>
            <th>Name</th>
            <th>Email</th>
            <th>Role</th>
            <th>Permissions</th>
            <th>Status</th>
            <th>Last Login</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.id}>
              <td>
                <FontAwesomeIcon 
                  icon={user.isAdmin ? faUserShield : faUser} 
                  className={user.isAdmin ? 'text-danger me-2' : 'text-secondary me-2'} 
                />
                {user.username}
              </td>
              <td>{user.firstName} {user.lastName}</td>
              <td>{user.email}</td>
              <td>
                <Badge bg={user.isAdmin ? 'danger' : 'secondary'}>
                  {user.isAdmin ? 'Admin' : 'User'}
                </Badge>
              </td>
              <td style={{ maxWidth: '300px' }}>
                {user.isAdmin ? (
                  <Badge bg="success">All Permissions</Badge>
                ) : (
                  renderPermissions(user.permissions)
                )}
              </td>
              <td>
                <Badge bg={user.isActive ? 'success' : 'secondary'}>
                  {user.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </td>
              <td>
                {user.lastLogin 
                  ? new Date(user.lastLogin).toLocaleString() 
                  : 'Never'}
              </td>
              <td>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="me-2"
                  onClick={() => handleEdit(user)}
                >
                  <FontAwesomeIcon icon={faEdit} />
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleDelete(user)}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* User Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalMode === 'add' ? 'Add New User' : 'Edit User'}
          </Modal.Title>
        </Modal.Header>
        <Form key={showModal ? 'form-open' : 'form-closed'} onSubmit={handleSubmit} autoComplete="off">
          <Modal.Body>
            <Tab.Container defaultActiveKey="basic">
              <Nav variant="tabs" className="mb-3">
                <Nav.Item>
                  <Nav.Link eventKey="basic">Basic Information</Nav.Link>
                </Nav.Item>
                {!formData.isAdmin && (
                  <Nav.Item>
                    <Nav.Link eventKey="permissions">Permissions</Nav.Link>
                  </Nav.Item>
                )}
              </Nav>

              <Tab.Content>
                <Tab.Pane eventKey="basic">
                  {/* Hidden dummy fields to trick browser autofill */}
                  <input type="text" style={{ display: 'none' }} />
                  <input type="password" style={{ display: 'none' }} />
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Username *</Form.Label>
                    <Form.Control
                      type="text"
                      name={`username-${Date.now()}`}
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      onFocus={(e) => e.target.removeAttribute('readonly')}
                      required
                      disabled={modalMode === 'edit'}
                      autoComplete="nope"
                      data-lpignore="true"
                      data-form-type="other"
                      readOnly
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Email *</Form.Label>
                    <Form.Control
                      type="email"
                      name={`email-${Date.now()}`}
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      onFocus={(e) => e.target.removeAttribute('readonly')}
                      required
                      autoComplete="nope"
                      data-lpignore="true"
                      data-form-type="other"
                      readOnly
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>
                      Password {modalMode === 'edit' && '(leave blank to keep current)'}
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showPassword ? 'text' : 'password'}
                        name={`password-${Date.now()}`}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        onFocus={(e) => e.target.removeAttribute('readonly')}
                        required={modalMode === 'add'}
                        placeholder={modalMode === 'edit' ? 'Leave blank to keep current password' : ''}
                        autoComplete="new-password"
                        data-lpignore="true"
                        data-form-type="other"
                        readOnly
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                      </Button>
                    </InputGroup>
                    <Form.Text className="text-muted">
                      Password must be at least 8 characters with uppercase, lowercase, number, and special character (!@#$%^&*)
                    </Form.Text>
                  </Form.Group>

                  <div className="row">
                    <div className="col">
                      <Form.Group className="mb-3">
                        <Form.Label>First Name *</Form.Label>
                        <Form.Control
                          type="text"
                          name={`firstName-${Date.now()}`}
                          value={formData.firstName}
                          onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                          onFocus={(e) => e.target.removeAttribute('readonly')}
                          required
                          autoComplete="nope"
                          data-lpignore="true"
                          data-form-type="other"
                          readOnly
                        />
                      </Form.Group>
                    </div>
                    <div className="col">
                      <Form.Group className="mb-3">
                        <Form.Label>Last Name *</Form.Label>
                        <Form.Control
                          type="text"
                          name={`lastName-${Date.now()}`}
                          value={formData.lastName}
                          onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                          onFocus={(e) => e.target.removeAttribute('readonly')}
                          required
                          autoComplete="nope"
                          data-lpignore="true"
                          data-form-type="other"
                          readOnly
                        />
                      </Form.Group>
                    </div>
                  </div>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Admin User (has all permissions)"
                      checked={formData.isAdmin}
                      onChange={(e) => setFormData({ ...formData, isAdmin: e.target.checked })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Active"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                  </Form.Group>
                </Tab.Pane>

                {!formData.isAdmin && (
                  <Tab.Pane eventKey="permissions">
                    <Alert variant="info" className="py-2 mb-3">
                      <small>
                        Select the modules and permissions this user should have access to.
                        <br />
                        <strong>Inventory permissions are hierarchical:</strong> Approve includes Submit and Read; Submit includes Read.
                      </small>
                    </Alert>

                    <div className="row g-3">
                      {Object.entries(MODULES).map(([moduleKey, moduleConfig]) => (
                        <div key={moduleKey} className="col-md-6">
                          <div className="border rounded p-2" style={{ height: '100%' }}>
                            <h6 className="mb-2 text-primary" style={{ fontSize: '0.9rem' }}>
                              {moduleConfig.label}
                              {moduleConfig.exclusive && <small className="text-muted ms-1">(choose one)</small>}
                            </h6>
                            {moduleConfig.permissions.map(permission => (
                              <Form.Check
                                key={`${moduleKey}-${permission}`}
                                type={moduleConfig.exclusive ? 'radio' : 'checkbox'}
                                name={moduleConfig.exclusive ? `${moduleKey}-permission` : undefined}
                                id={`${moduleKey}-${permission}`}
                                label={
                                  <small>
                                    {permission === 'full_access' && 'Full Access'}
                                    {permission === 'read_only' && 'Read Only (view only)'}
                                    {permission === 'submit' && 'Submit (view + submit orders)'}
                                    {permission === 'approve' && 'Approve (full access)'}
                                  </small>
                                }
                                checked={formData.permissions[moduleKey]?.includes(permission) || false}
                                onChange={(e) => handlePermissionChange(moduleKey, permission, e.target.checked)}
                                className="mb-1"
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>

                    {Object.keys(formData.permissions).length === 0 && (
                      <Alert variant="warning" className="mt-3 py-2">
                        <small>No permissions selected. This user will not be able to access any modules.</small>
                      </Alert>
                    )}
                  </Tab.Pane>
                )}
              </Tab.Content>
            </Tab.Container>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {modalMode === 'add' ? 'Create User' : 'Save Changes'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}

export default Users;

